import { ContractType, DealStage } from './deal';
import { ITalent, RemoteWork } from './talent';
import { ATSOrigin, IBloomer, IContact, ISociety } from './types.shared';

export enum Staff {
  tam = 'tam',
  csm = 'csm',
  sales = 'sales',
  system = 'system',
}

export enum JobType {
  Freelance = 'Freelance',
  Freelance_Tech_Digital = 'Freelance_Tech_Digital',
  Freelance_Bloomays_People = 'Freelance_Bloomays_People',
  Freelance_RPO = 'Freelance_RPO',
  CDI = 'CDI',
  CDI_Tech_Digital = 'CDI_Tech_Digital',
  CDI_Bloomays_People = 'CDI_Bloomays_People',
  People = 'People',
  RPO = 'RPO',
}

/** Variantes freelance de {@link JobType} — primes mission (`MissionPrime`). */
export type FreelanceJobType =
  | JobType.Freelance
  | JobType.Freelance_Tech_Digital
  | JobType.Freelance_Bloomays_People
  | JobType.Freelance_RPO;

/** Variantes CDI de {@link JobType} — primes facturation (`BillingPrime`). */
export type CdiJobType = JobType.CDI | JobType.CDI_Tech_Digital | JobType.CDI_Bloomays_People;

const FREELANCE_JOB_TYPE_LIST = [
  JobType.Freelance,
  JobType.Freelance_Tech_Digital,
  JobType.Freelance_Bloomays_People,
  JobType.Freelance_RPO,
] as const satisfies readonly FreelanceJobType[];

const CDI_JOB_TYPE_LIST = [
  JobType.CDI,
  JobType.CDI_Tech_Digital,
  JobType.CDI_Bloomays_People,
] as const satisfies readonly CdiJobType[];

export function isFreelanceJobType(jobType: JobType): jobType is FreelanceJobType {
  return (FREELANCE_JOB_TYPE_LIST as readonly JobType[]).includes(jobType);
}

export function isCdiJobType(jobType: JobType): jobType is CdiJobType {
  return (CDI_JOB_TYPE_LIST as readonly JobType[]).includes(jobType);
}

export enum MissionType {
  IT = 'IT',
  RPO = 'RPO',
  People = 'People',
}

export enum DealType {
  CDI_TECH = 'CDI_TECH',
  CDI_PEOPLE = 'CDI_PEOPLE',
  IT = 'IT',
  RPO = 'RPO',
  People = 'People',
}

/** Variantes mission freelance de {@link DealType} — Win mission (IT / RPO / People). */
export type FreelanceMissionDealType = DealType.IT | DealType.RPO | DealType.People;

const FREELANCE_MISSION_DEAL_TYPE_LIST = [
  DealType.IT,
  DealType.RPO,
  DealType.People,
] as const satisfies readonly FreelanceMissionDealType[];

export function isFreelanceMissionDealType(dealType: DealType): dealType is FreelanceMissionDealType {
  return (FREELANCE_MISSION_DEAL_TYPE_LIST as readonly DealType[]).includes(dealType);
}

export enum PriorityType {
  low = 'low',
  medium = 'medium',
  high = 'high',
}

export enum LostReasonType {
  recherche_trop_complexe = 'recherche_trop_complexe',
  client_indecis = 'client_indecis',
  project_client_annule = 'project_client_annule',
  recrute_via_reseau_client = 'recrute_via_reseau_client',
  recrute_via_concurrent = 'recrute_via_concurrent',
  client_ghost = 'client_ghost',
  pas_exclu = 'pas_exclu',
  client_pas_reactif = 'client_pas_reactif',
  job_deja_place_avec_candidat_bloomays = 'job_deja_place_avec_candidat_bloomays',
  autre = 'autre',
  on_refuse_job = 'on_refuse_job',
  concurrence = 'concurrence',
  prix = 'prix',
  timing = 'timing',
  project_bloomays_annule = 'project_bloomays_annule',
  client_annule_post_win = 'client_annule_post_win',
  bloomer_annule_post_win = 'bloomer_annule_post_win',
  client_arrete_process_indecis = 'client_arrete_process_indecis',
  client_arrete_process_pas_assez_resultats = 'client_arrete_process_pas_assez_resultats',
  projet_client_annule_manque_budget = 'projet_client_annule_manque_budget',
  /** @deprecated Use bloomer_annule_post_win. Prévu pour migration Pipefy→PostgreSQL, non utilisé en base. */
  refus_candidat = 'refus_candidat',
}

export enum BloomerSourceType {
  site_bloomays = 'Candidat ayant postulé sur le site Bloomays',
  outil_recherche_bloomays = 'Outil de recherche Bloomays (App)',
  linkedin = 'Linkedin',
  turnover_it = 'Turnover IT',
  pylote = 'Pylote',
  recommandation = 'Recommandation',
  hellowork = 'HelloWork',
  google = 'Google',
  apec = 'Apec',
  reseau_perso = 'Réseau perso',
  whatsapp = 'Whatsapp',
  collective = 'Collective',
  autre = 'Autre',
  hiresweet = 'Hiresweet',
}

export type SalesProperties = {
  shouldBeCountAsDeal: boolean;
  commercialMonth: Date | null;
  sendJobQualifEmail?: boolean | null;
  hubspotDealId?: string;
  leadOrigin: string;
  leadCanal: string;
  leadCanalDetail: string;
  leadCustomerStatus: string;
  missionStartDate?: string;
  missionEndDate?: string;
};

export type ISalesDeal = {
  id: string;
  stage: DealStage;
  society: ISociety;
  recruiter: IBloomies;
} & SalesProperties &
  IJobProperties;

export enum StatusType {
  win = 'WIN',
  lost = 'LOST',
  inProgress = 'inProgress',
}

export type IJobProperties = {
  jobOwner: IBloomies;
  name: string;
  jobType: JobType;
  dealType: DealType;
  jobTitle: string;
  interlocuteur1Email: string;
  interlocuteur2Email: string | null;
  onSiteDaysPerWeek: number;
  location: string;
  // quickwin attributes
  duration: number | null;
  remoteDaysPerWeek: number | null;
  expectedStartDate: string | null;
  activitySector: string | null;
  context: string | null;
  details: string | null;
  team: string | null;
  minYearsXP: number | null;
  scoreCard1: string | null;
  scoreCard2: string | null;
  scoreCard3: string | null;
  scoreCard4: string | null;
  requiredLanguages: string | null;
  confidentialNotes: string | null;
  maxClientBudget: number | null;
  clientBudgetDetails: string | null;
  maxTalentBudget: number | null;
  outOfBudgetProfil: string | null;
  budgetDecisionMaker: string | null;
  isInternalValidation: boolean | null;
  clientResearchTrigger: string | null;
  interviewProcess: string | null;
  finalValidationResponsable: string | null;
  minTotalProfilesToInclude: string | null;
  searchStatus: string | null;
  competitors: string | null;
  isExclusivity: boolean | null;
  cvDebriefDate: string | null;
  isResponsiveClient: boolean | null;
  isReliableClient: boolean | null;
  isDirectLinkWithDecisionMaker: boolean | null;
  priority: PriorityType | null;
  isBloomerReplacement: boolean | null;
  isQuickContract: boolean | null;
  // win or lost attributs - pour peupler ces données sur hiresweet il faut passer un candidat Id, ces valeurs viennent des notes
  status?: StatusType;
  winDate: Date | null;
  lostReason?: LostReasonType;
  otherLostReason?: string;
  bloomerSource?: BloomerSourceType;
  passeur?: string;
  buteur?: string;
  capitaine?: string;
  defenseur?: string;
  createdAt: Date;
};

/** Séniorité TAM (alignée enum Prisma `TamSeniority`, vocabulaire métier primes). */
export type BloomieTamSeniority =
  | 'Apprenti'
  | 'Associate'
  | 'Confirme'
  | 'Senior'
  | 'Principal'
  | 'Executive'
  | 'Director';

export type IBloomies = {
  firstname: string;
  lastname: string;
  email: string;
  externalId?: string;
  role: Staff;
  hubspotUserId?: string;
  hubspotLabelNameId?: string;
  phoneNumber?: string;
  avatarUrl?: string;
  slackId?: string;
  airtableId?: string;
  seniority?: BloomieTamSeniority | null;
};

export type IJobFunnelProject = {
  id: string;
  society: ISociety;
  recruiter: IBloomies;
  airtableId?: string;
  pipefyId?: string;
  hiresweetId?: string;
  isArchived?: boolean;
} & IJobProperties;

export type Candidat = {
  lastname: string;
  firstname: string;
  jobTitle?: string;
  profilPictureUrl?: string;
  email: string;
  phoneNumber?: string;
  linkedInUrl?: string;
  externalId?: string;
};

export type CardInfo = { url: string; id: string; title: string };

export type ITalentPhaseNotes = {
  id: string;
  jobId: string;
  talentId: string;
  phaseId: TalentNotesPhase;
  date: Date;
  answers: Record<string, string>;
  source: 'Pipefy' | 'Hiresweet';
  externalId: string;
  author: string;
};

export type TalentNotesPhase =
  | 'Préparation interview'
  | 'Débrief post-interview avec client'
  | 'Débrief post-interview'
  | 'Positionnement'
  | 'Non validé'
  | 'Prise de références'
  | 'Qualification'
  | 'Pré-Qualification'
  | 'Présenté au Client';

export interface PhaseNotesQuestions {
  id: string;
  pipefyId?: string;
  hiresweetId?: string;
  question: string;
  order: number;
}

export type JobFunnelWithTalents = ISalesDeal & {
  talents: ITalent[];
};

export type PhaseNoteQuestionAnswer = {
  question: string;
  answer: string;
};

export type PhaseNoteData = {
  jobName: string;
  societyName: string;
  phase: string;
  date: Date;
  answers: PhaseNoteQuestionAnswer[];
  author: string;
};

export enum BusinessType {
  IT = 'IT',
  RPO = 'RPO',
  People = 'People',
}

export enum BillingType {
  Recruitement = 'Recruitement',
  Audit = 'Audit',
}

export enum PurchaseOrderStatus {
  waiting = 'waiting',
  received = 'received',
  expired = 'expired',
  soldout = 'soldout',
}

export type InvoiceStatus =
  | 'Non créée'
  | 'En erreur'
  | 'Créée à valider'
  | 'Créée et finalisée'
  | 'En cours'
  | 'Annulée';

export enum PaymentTerms {
  FIFTY_FIFTY_SIGNATURE_START = 'FIFTY_FIFTY_SIGNATURE_START', // 50% signature et 50% démarrage
  FIFTY_FIFTY_START_TRIAL = 'FIFTY_FIFTY_START_TRIAL', // 50% démarrage et 50% fin première PE
  FULL_START = 'FULL_START', // 100% au démarrage
  CUSTOM = 'CUSTOM', // conditions particulières
}

export interface Billing {
  recordId: string;
  externalId?: string;
  type: BillingType;
  billingDate: string;
  trialPeriodEndDate: string;
  customerRecordId?: string;
  customerName?: string;
  salesPriceWithoutTaxes: number;
  billingContactRecordId: string[];
  billingContactFullname: string[];
  billed: boolean;
  accountManagerRecordId: string[];
  accountManager?: IBloomies;
  paydateDays: number;
  description: string;
  nextStep?: string;
  legalContracts?: string[];
  suiviCardId?: string;
  createdAt: string;
  origin: ATSOrigin;
  paymentTerms?: PaymentTerms;
  externalCustomerInvoiceId?: string;
  externalCustomerInvoiceUrl?: string;
  replacement: boolean;
  paymentTermsDetails?: string;
  cancelled: boolean;
  bdc?: string;
  businessType: BusinessType;
  taxes: number;
  lastCustomerInvoiceError?: string;
  invoiceStatus: InvoiceStatus;
  bloomerRecord?: string;
  // attributs à populer (soit pas graphql, soit pas un autre moyen)
  customer?: ISociety[];
  billingContact?: IContact[];
  bloomer?: IBloomer;
}

export type ATSJobOffer = {
  id: string;
  jobApplyUrl: string;
  jobTitle: string;
  jobDescription: string;
  businessTypeLabel?: 'Tech' | 'People' | 'Autre';
  businessType?: BusinessType;
  locationLabel?: string;
  missionTypeLabel: 'Freelance' | 'CDI' | 'CDD';
  missionType: JobType;
  published: boolean;
  publishedDate: Date;
  remoteLabel: string;
  remote: RemoteWork;
  xpLabel?: string;
  salaryLabel: string;
  salaryCurrency: string;
  salaryMin: number;
  salaryMax: number;
  salaryPeriod: 'day' | 'month' | 'year';
};

export enum JobFunnelPhase {
  NotContacted = 'NotContacted',
  Contacted = 'Contacted',
  Replied = 'Replied',
  Prequalification = 'Prequalification',
  Qualification = 'Qualification',
  Positionnement = 'Positionnement',
  PresentedToClient = 'PresentedToClient',
  PreparationITW = 'PreparationITW',
  DebriefITW = 'DebriefITW',
  ValidationInfos = 'ValidationInfos',
  BPA = 'BPA',
  Validated = 'Validated',
  Archive = 'Archive',
}

export enum JobFunnelPhaseLabel {
  NotContacted = 'Non contactés',
  Contacted = 'Contactés',
  Replied = 'Répondus',
  Prequalification = 'Pré-qualification',
  Qualification = 'Qualification',
  Positionnement = 'Positionnement',
  PresentedToClient = 'Présenté au Client',
  PreparationITW = 'Prépa ITW',
  DebriefITW = 'Debrief ITW',
  ValidationInfos = 'Validation des infos',
  BPA = 'BPA envoyé',
  Validated = 'Validés',
  Archive = 'Archivés',
}

export enum TalentBoardJobFunnelPhase {
  NotValidated = 'NotValidated',
  Qualification = 'Qualification',
  Positionnement = 'Positionnement',
  ITWClient = 'ITWClient',
}

export type TalentBoardComment = {
  id: string;
  jobId: string;
  talentId: string;
  comment: string;
  author: string;
  createdAt: Date;
  updatedAt: Date;
};

export type TalentBoardInvitation = {
  id: string;
  jobId: string;
  email: string;
  createdAt: Date;
  createdBy: string;
  resentAt: Date | null;
};

export enum TalentBoardEventType {
  TalentboardOpenJob = 'talentboard-open-job',
  TalentboardOpenTalent = 'talentboard-open-talent',
  TalentboardUpsertComment = 'talentboard-upsert-comment',
  TalentboardInvitationCreate = 'talentboard-invitation-create',
  TalentboardInvitationDelete = 'talentboard-invitation-delete',
  TalentboardTalentMarkedAsNotSeen = 'talentboard-talent-marked-as-not-seen',
  TalentboardTalentMarkedAsSeen = 'talentboard-talent-marked-as-seen',
  TalentboardOpenContactEmail = 'talentboard-open-contact-email',
  TalentboardOpenContactPhone = 'talentboard-open-contact-phone',
  TalentboardResetFilters = 'talentboard-reset-filters',
  TalentboardListJobs = 'talentboard-list-jobs',
  TalentboardOpenJobTalentBoard = 'talentboard-open-job-talent-board',
  TalentboardOpenJobDetails = 'talentboard-open-job-details',
  TalentboardOpenShareJob = 'talentboard-open-share-job',
  TalentboardInvitationResend = 'talentboard-invitation-resend',
  TalentboardFilterJobBySearchQuery = 'talentboard-filter-job-by-search-query',
  TalentboardFilterJobByStatus = 'talentboard-filter-job-by-status',
  TalentboardFilterJobByTam = 'talentboard-filter-job-by-tam',
  TalentboardSortJobByStartDate = 'talentboard-sort-job-by-start-date',
  TalentboardFilterTalentBySearchQuery = 'talentboard-filter-talent-by-search-query',
  TalentboardFilterByDate = 'talentboard-filter-by-date',
  TalentboardFilterByTopProfile = 'talentboard-filter-by-top-profile',
  TalentboardFilterByNotMarkedAsSeen = 'talentboard-filter-by-not-marked-as-seen',
  TalentboardOpenNotesTalent = 'talentboard-open-notes-talent',
  TalentboardOpenProfileTalent = 'talentboard-open-profile-talent',
  TalentboardOpenExperienceProfile = 'talentboard-open-experience-profile',
  TalentboardOpenFormationProfile = 'talentboard-open-formation-profile',
  TalentboardOpenCvProfile = 'talentboard-open-cv-profile',
  TalentboardRequestMagicLink = 'talentboard-request-magic-link',
  TalentboardGoToAskMagicLink = 'talentboard-go-to-ask-magic-link',
  TalentboardJoblistPaginationChangePage = 'talentboard-joblist-pagination-change-page',
  TalentboardJoblistPaginationChangeRowsPerPage = 'talentboard-joblist-pagination-change-rows-per-page',
  TalentboardOpenEventsDashboard = 'talentboard-open-events-dashboard',
}

export enum TalentBoardPersistedEventType {
  TalentboardOpenJob = 'TalentboardOpenJob',
  TalentboardOpenTalent = 'TalentboardOpenTalent',
  TalentboardUpsertComment = 'TalentboardUpsertComment',
  TalentboardInvitationCreate = 'TalentboardInvitationCreate',
  TalentboardInvitationDelete = 'TalentboardInvitationDelete',
  TalentboardTalentMarkedAsNotSeen = 'TalentboardTalentMarkedAsNotSeen',
}

export type TalentBoardEventTalentData = { talentId: string; talentFullName: string };
export type TalentBoardEventInvitationData = { email: string };

type TalentBoardEventWithData<T extends TalentBoardPersistedEventType, U> = {
  type: T;
  data: U;
  id: string;
  jobId: string;
  date: Date;
  authorEmail: string;
};

type TalentBoardEventWithoutData<T extends TalentBoardPersistedEventType> = {
  type: T;
  id: string;
  jobId: string;
  date: Date;
  authorEmail: string;
  data?: undefined;
};

export type TalentBoardEventOpenJob = TalentBoardEventWithoutData<TalentBoardPersistedEventType.TalentboardOpenJob>;
export type TalentBoardEventOpenTalent = TalentBoardEventWithData<
  TalentBoardPersistedEventType.TalentboardOpenTalent,
  TalentBoardEventTalentData
>;
export type TalentBoardEventUpsertComment = TalentBoardEventWithData<
  TalentBoardPersistedEventType.TalentboardUpsertComment,
  TalentBoardEventTalentData
>;
export type TalentBoardEventInvitationCreate = TalentBoardEventWithData<
  TalentBoardPersistedEventType.TalentboardInvitationCreate,
  TalentBoardEventInvitationData
>;
export type TalentBoardEventInvitationDelete = TalentBoardEventWithData<
  TalentBoardPersistedEventType.TalentboardInvitationDelete,
  TalentBoardEventInvitationData
>;
export type TalentBoardEventTalentMarkedAsNotSeen = TalentBoardEventWithData<
  TalentBoardPersistedEventType.TalentboardTalentMarkedAsNotSeen,
  TalentBoardEventTalentData
>;

export type TalentBoardEvent =
  | TalentBoardEventOpenJob
  | TalentBoardEventOpenTalent
  | TalentBoardEventUpsertComment
  | TalentBoardEventInvitationCreate
  | TalentBoardEventInvitationDelete
  | TalentBoardEventTalentMarkedAsNotSeen;

export type TalentBoardEventData = TalentBoardEventTalentData | TalentBoardEventInvitationData | undefined;

export type TalentBoardEventKpis = {
  talentBoardOpened: number;
  talentViewed: number;
  commentUpserted: number;
};

type Person = {
  email: string;
  firstname: string;
  lastname: string;
  phoneNumber?: string;
};

type Bloomer = Person & {
  id: string;
  externalId?: string;
};

type Society = {
  id: string;
  name: string;
  externalId?: string;
};

export type Mission = {
  id: string;
  label: string;
  beginMission: Date;
  endMission: Date;
  externalId: string;
  origin: ATSOrigin;
  type: BusinessType;
  contractType: ContractType;
  createdAt: Date;
  replacement: boolean;
  shouldBeCountAsDeal: boolean;
  purchaseOrderRequired?: boolean;

  bloomer: Bloomer & {
    billingSociety?: Partial<Society>;
  };

  clientSociety: Partial<Society>;

  /** Record IDs tarifs Airtable liés à la mission (champ `pricing` côté Airtable). */
  pricingIds: string[];
};

export type CRAStatus =
  | 'Non créé'
  | 'Validé client'
  | 'Attente signature Client'
  | 'Attente signature Bloomer'
  | 'Signature non créee'
  | 'Forcé Bloomays'
  | 'Non travaillé';

export type Cra = {
  month: Date;
  normalWorkedDays: number;
  saturdaysWorkedDays: number;
  sundayAndholidaysWorkedDays: number;
  astreinte: number;
  status: CRAStatus;

  bloomerId: string;
  missionId: string;
};

export type Pricing = {
  id: string;
  purchasePricePerDayWithoutTaxes: number;
  salePricePerDayWithoutTaxes: number;
  astreinteCoef: number; // default to 2
  startDate: Date;

  missionId: string;
};

/** Source du contact retenu pour les flux PDS (avenant client avec ou sans doc à signer). */
export type ClientPdsContactSource = 'clientLegal' | 'contactOperations';

/** Source du contact retenu pour SST (fournisseur) selon la matrice v2. */
export type SupplierLegalContactSource = 'bloomerContact' | 'portageLegalContact' | 'bloomerLegalContact';

export type PurchaseOrder = {
  id: string;
  recordId?: string;
  orderNumber: string;
  missionsRecordId?: string[];
  clientSocietyRecordId?: string;
  fileUrl?: string;
  startDate?: Date;
  endDate?: Date;
  amountHT?: number;
  remainingAmount?: number;
  useForNonWorkingHours?: boolean;
  invoiceComment?: string;
  status: PurchaseOrderStatus;
};
